import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Activity, ArrowRight, AtSign, BadgeCheck, Building2, Coins, Flag, Gauge, Hash, KeyRound, Megaphone, MessageCircle, Phone,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { StatusPill } from '@/components/StatusPill';
import { LoadErrorState } from '@/components/ListStates';
import { ConnectMetaButton } from '@/components/facebook/ConnectMetaButton';
import { SettingsCard, SettingsPage, SettingsRow, SettingsSection } from '@/components/settings/SettingsPage';
import { settingsService, IntegrationsSummary } from '@/services/settings.service';
import { metaOAuthService } from '@/services/meta-oauth.service';

const HEADER = {
  eyebrow: 'Channels',
  title: 'Integrations',
  description: 'The WhatsApp number and the Meta ad account this workspace uses. Check them, connect them or disconnect them here.',
};

/** Meta's own wording in WhatsApp Manager for the GREEN / YELLOW / RED quality rating. */
const QUALITY: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' }> = {
  GREEN: { label: 'High', variant: 'success' },
  YELLOW: { label: 'Medium', variant: 'warning' },
  RED: { label: 'Low', variant: 'danger' },
};

function QualityRating({ rating }: { rating?: string }) {
  if (!rating) return <span className="text-text-muted">—</span>;
  const known = QUALITY[rating.toUpperCase()];
  if (known) return <StatusPill status={rating} label={known.label} variant={known.variant} />;
  return <span>{rating.charAt(0) + rating.slice(1).toLowerCase()}</span>;
}

function ConnectedFlag({ connected }: { connected: boolean }) {
  return <StatusPill status={connected ? 'connected' : 'missing'} label={connected ? 'Connected' : 'Not connected'} variant={connected ? 'success' : 'warning'} />;
}

/** Keeps a long name from pushing the row wider than its card. */
function Clip({ children, mono }: { children: ReactNode; mono?: boolean }) {
  return <span className={clsx('block max-w-[10rem] truncate @md:max-w-[16rem]', mono && 'font-mono text-xs')}>{children}</span>;
}

function daysUntil(iso?: string): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

function TokenCountdown({ tokenExpiresAt }: { tokenExpiresAt?: string }) {
  const days = daysUntil(tokenExpiresAt);
  if (days === null) return <span className="text-text-muted">—</span>;
  if (days < 0) return <StatusPill status="expired" label="Expired" variant="danger" />;
  return (
    <StatusPill
      status="token"
      label={days === 0 ? 'Expires today' : `${days} day${days === 1 ? '' : 's'} left`}
      variant={days < 14 ? 'warning' : 'neutral'}
    />
  );
}

function ConnectPrompt({ icon, title, text, children }: { icon: ReactNode; title: string; text: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-2 py-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white shadow-soft">{icon}</div>
      <div className="text-sm font-medium text-text-primary">{title}</div>
      <p className="max-w-sm text-xs text-text-muted">{text}</p>
      {children}
    </div>
  );
}

export default function IntegrationsSettings() {
  const [data, setData] = useState<IntegrationsSummary | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = () => {
    setLoadError(false);
    return settingsService.integrations().then(setData).catch(() => setLoadError(true));
  };

  useEffect(() => {
    void load();
  }, []);

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Facebook and Instagram from this workspace? You will need to connect again to use ads.')) return;
    setDisconnecting(true);
    try {
      await metaOAuthService.disconnect();
      toast.success('Meta disconnected');
      await load();
    } catch {
      // interceptor toast
    } finally {
      setDisconnecting(false);
    }
  };

  if (!data) {
    return (
      <SettingsPage {...HEADER}>
        <div className="py-7">
          {loadError ? (
            <LoadErrorState what="your integrations" onRetry={() => void load()} />
          ) : (
            <div className="space-y-4"><div className="skeleton h-64 rounded-2xl" /><div className="skeleton h-72 rounded-2xl" /></div>
          )}
        </div>
      </SettingsPage>
    );
  }

  const wa = data.whatsapp;
  const fb = data.facebook;

  return (
    <SettingsPage {...HEADER}>
      <SettingsSection
        title="WhatsApp"
        description="The WhatsApp Business number your team chats from and sends campaigns with."
      >
        {wa ? (
          <SettingsCard
            padded={false}
            footer={(
              <Link to="/whatsapp-account">
                <Button variant="secondary" iconRight={<ArrowRight size={13} />}>Manage WhatsApp account</Button>
              </Link>
            )}
          >
            <div className="divide-y divide-border">
              <SettingsRow
                icon={<MessageCircle size={16} />}
                label="Status"
                value={<StatusPill status={wa.isActive ? 'active' : 'inactive'} label={wa.isActive ? 'Connected' : 'Not in use'} variant={wa.isActive ? 'success' : 'neutral'} />}
              />
              <SettingsRow icon={<Building2 size={16} />} label="Business name" value={<Clip>{wa.businessName}</Clip>} />
              <SettingsRow icon={<Phone size={16} />} label="Phone number" value={<Clip mono>{wa.displayPhoneNumber}</Clip>} />
              <SettingsRow icon={<BadgeCheck size={16} />} label="Verified name" value={<Clip>{wa.verifiedName}</Clip>} />
              <SettingsRow icon={<Activity size={16} />} label="Quality rating" value={<QualityRating rating={wa.qualityRating} />} />
              <SettingsRow icon={<Gauge size={16} />} label="Messaging limit tier" value={wa.messagingLimitTier ?? '—'} />
            </div>
          </SettingsCard>
        ) : (
          // Customers connect their own number through Meta's Embedded Signup; sending them to support was a dead end.
          <SettingsCard>
            <ConnectPrompt
              icon={<MessageCircle size={20} />}
              title="No WhatsApp number connected"
              text="Connect your WhatsApp Business number with Meta in a few steps."
            >
              <Link to="/whatsapp-accounts">
                <Button iconRight={<ArrowRight size={13} />}>Connect WhatsApp</Button>
              </Link>
            </ConnectPrompt>
          </SettingsCard>
        )}
      </SettingsSection>

      <SettingsSection
        title="Facebook & Instagram"
        description="The Meta ad account your ads run on, with the Facebook Page and Instagram account linked to it."
      >
        {fb ? (
          <SettingsCard
            padded={false}
            footer={(
              <>
                <Button variant="ghost" loading={disconnecting} onClick={handleDisconnect} className="mr-auto">Disconnect</Button>
                <Link to="/facebook/ad-account">
                  <Button variant="secondary" iconRight={<ArrowRight size={13} />}>Manage ad account</Button>
                </Link>
                <ConnectMetaButton label="Reconnect" variant="secondary" onConnected={load} />
              </>
            )}
          >
            <div className="divide-y divide-border">
              <SettingsRow
                icon={<Megaphone size={16} />}
                label="Status"
                value={<StatusPill status={fb.isActive ? 'active' : 'inactive'} label={fb.isActive ? 'Connected' : 'Not active'} variant={fb.isActive ? 'success' : 'neutral'} />}
              />
              <SettingsRow icon={<Building2 size={16} />} label="Ad account" value={<Clip>{fb.adAccountName}</Clip>} />
              <SettingsRow icon={<Hash size={16} />} label="Ad account ID" value={<Clip mono>{fb.adAccountId}</Clip>} />
              <SettingsRow icon={<Coins size={16} />} label="Currency" value={fb.currency} />
              <SettingsRow icon={<Flag size={16} />} label="Facebook Page" value={<ConnectedFlag connected={!!fb.pageId} />} />
              <SettingsRow icon={<AtSign size={16} />} label="Instagram account" value={<ConnectedFlag connected={!!fb.instagramAccountId} />} />
              {fb.oauthGrantedAt && (
                <SettingsRow icon={<KeyRound size={16} />} label="Meta access" value={<TokenCountdown tokenExpiresAt={fb.tokenExpiresAt} />} />
              )}
              {fb.tokenRefreshError && (
                <div className="px-5 py-3.5">
                  <div role="alert" className="rounded-xl bg-danger-soft px-3.5 py-2.5 text-xs leading-relaxed text-danger">
                    Meta couldn’t renew the connection: {fb.tokenRefreshError}. Reconnect to give fresh access.
                  </div>
                </div>
              )}
            </div>
          </SettingsCard>
        ) : (
          <SettingsCard>
            <ConnectPrompt
              icon={<Megaphone size={20} />}
              title="No Meta ad account connected"
              text="Connect with Meta to give access and pick the ad account your Facebook and Instagram ads run on."
            >
              <ConnectMetaButton onConnected={load} />
            </ConnectPrompt>
            <details className="mt-2 border-t border-border pt-4 text-xs text-text-muted">
              <summary className="cursor-pointer select-none hover:text-text-secondary">Use a system-user token instead (advanced)</summary>
              <p className="mt-2 rounded-xl bg-surface-alt px-3.5 py-3 leading-relaxed">
                If your team uses a system-user token instead of signing in with Meta, contact Slightor support to add it for you.
                Signing in with Meta is the recommended way.
              </p>
            </details>
          </SettingsCard>
        )}
      </SettingsSection>
    </SettingsPage>
  );
}
